import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calculator,
  ImagePlus,
  LoaderCircle,
  Plus,
  RefreshCw,
  Save,
  Send,
  Trash2,
  UploadCloud
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type {
  PlatformCategory,
  PlatformCategoryAttribute,
  PricingTemplate,
  ProductDetail,
  ProductImage,
  ProductVariant,
  Store
} from "../lib/types";
import { money, platformLabel, statusLabel } from "../lib/format";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

type EditorTab = "basic" | "channel" | "images" | "variants" | "pricing" | "publish";

interface ChannelConfigDraft {
  storeId: string;
  categoryId: string;
  attributes: Record<string, unknown>;
}

interface CategoryTreeNode extends PlatformCategory {
  children: CategoryTreeNode[];
}

const emptyVariant = (sku: string, currency = "USD"): ProductVariant => ({
  sku,
  variant_name: "默认规格",
  attributes_json: "{}",
  cost_price: 0,
  price: 0,
  currency,
  stock: 0,
  weight: 0,
  image_url: ""
});

export function ProductEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const editing = Boolean(id && id !== "new");
  const [tab, setTab] = useState<EditorTab>("basic");
  const [name, setName] = useState("");
  const [spu, setSpu] = useState("");
  const [sku, setSku] = useState("");
  const [category, setCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brand, setBrand] = useState("");
  const [productType, setProductType] = useState("");
  const [description, setDescription] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourcePlatform, setSourcePlatform] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [status, setStatus] = useState("draft");
  const [images, setImages] = useState<ProductImage[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([emptyVariant("")]);
  const [attributeRows, setAttributeRows] = useState<Array<{ key: string; value: string }>>([
    { key: "", value: "" }
  ]);
  const [channelConfigs, setChannelConfigs] = useState<ChannelConfigDraft[]>([]);
  const [channelStoreId, setChannelStoreId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [notice, setNotice] = useState("");

  const productQuery = useQuery({
    queryKey: ["catalog-product", id],
    queryFn: () => api.get<{ product: ProductDetail }>(`/api/catalog/products/${id}`),
    enabled: editing
  });
  const storesQuery = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<{ stores: Store[] }>("/api/stores")
  });
  const templatesQuery = useQuery({
    queryKey: ["pricing-templates"],
    queryFn: () => api.get<{ templates: PricingTemplate[] }>("/api/catalog/pricing-templates")
  });
  const activeChannel = channelConfigs.find((config) => config.storeId === channelStoreId);
  const activeStore = (storesQuery.data?.stores ?? []).find(
    (store) => store.id === channelStoreId
  );
  const categoriesQuery = useQuery({
    queryKey: ["platform-categories", channelStoreId],
    queryFn: () =>
      api.get<{ categories: PlatformCategory[] }>(
        `/api/catalog/categories?storeId=${encodeURIComponent(channelStoreId)}&limit=5000`
      ),
    enabled: Boolean(channelStoreId)
  });
  const attributesQuery = useQuery({
    queryKey: ["platform-category-attributes", activeChannel?.categoryId],
    queryFn: () =>
      api.get<{
        category: PlatformCategory;
        attributes: PlatformCategoryAttribute[];
      }>(
        `/api/catalog/categories/${encodeURIComponent(activeChannel?.categoryId ?? "")}/attributes`
      ),
    enabled: Boolean(activeChannel?.categoryId)
  });
  const syncCategories = useMutation({
    mutationFn: () => {
      if (!activeStore) throw new Error("请先选择店铺");
      return api.post<{ total: number; message: string }>("/api/catalog/categories/sync", {
        platform: activeStore.platform,
        storeId: activeStore.id
      });
    },
    onSuccess: async (result) => {
      setNotice(result.message);
      await queryClient.invalidateQueries({
        queryKey: ["platform-categories", channelStoreId]
      });
    },
    onError: (error) => setNotice(error.message)
  });
  const syncAttributes = useMutation({
    mutationFn: () => {
      if (!activeChannel?.categoryId) throw new Error("请先选择叶子类目");
      return api.post<{ total: number; message: string }>(
        `/api/catalog/categories/${activeChannel.categoryId}/attributes/sync`
      );
    },
    onSuccess: async (result) => {
      setNotice(result.message);
      await queryClient.invalidateQueries({
        queryKey: ["platform-category-attributes", activeChannel?.categoryId]
      });
    },
    onError: (error) => setNotice(error.message)
  });

  useEffect(() => {
    const product = productQuery.data?.product;
    if (!product) return;
    setName(product.name);
    setSpu(product.spu);
    setSku(product.sku);
    setCategory(product.category);
    setCategoryId(product.category_id ?? "");
    setBrand(product.brand);
    setProductType(product.product_type ?? "");
    setDescription(product.description ?? "");
    setSourceUrl(product.source_url ?? "");
    setSourcePlatform(product.source_platform ?? "");
    setCurrency(product.currency);
    setStatus(product.status);
    try {
      const attributes = JSON.parse(product.attributes_json || "{}") as Record<string, unknown>;
      const rows = Object.entries(attributes).map(([key, value]) => ({
        key,
        value: typeof value === "string" ? value : JSON.stringify(value)
      }));
      setAttributeRows(rows.length > 0 ? rows : [{ key: "", value: "" }]);
    } catch {
      setAttributeRows([{ key: "", value: "" }]);
    }
    setImages(product.images);
    setVariants(product.variants.length > 0 ? product.variants : [emptyVariant(product.sku, product.currency)]);
    const productChannels = (product.channelConfigs ?? []).map((channel) => ({
        storeId: channel.store_id,
        categoryId: channel.category_id,
        attributes: JSON.parse(channel.attributes_json || "{}") as Record<string, unknown>
      }));
    setChannelConfigs(productChannels);
    if (productChannels[0]?.storeId) {
      setChannelStoreId(productChannels[0].storeId);
    }
  }, [productQuery.data]);

  useEffect(() => {
    if (channelStoreId) return;
    const firstConfiguredStore = channelConfigs[0]?.storeId;
    const firstStore = storesQuery.data?.stores[0]?.id;
    if (firstConfiguredStore || firstStore) {
      setChannelStoreId(firstConfiguredStore ?? firstStore ?? "");
    }
  }, [channelConfigs, channelStoreId, storesQuery.data]);

  const payload = useMemo(
    () => ({
      spu: spu || sku,
      sku,
      name,
      description,
      category,
      categoryId,
      brand,
      productType,
      sourceUrl,
      sourcePlatform,
      currency,
      status,
      attributes: Object.fromEntries(
        attributeRows
          .filter((row) => row.key.trim())
          .map((row) => [row.key.trim(), row.value])
      ),
      costPrice: variants[0]?.cost_price ?? 0,
      images: images.map((image, index) => ({
        url: image.url,
        alt: image.alt || name,
        isPrimary: index === 0
      })),
      variants: variants.map((variant) => ({
        sku: variant.sku,
        variantName: variant.variant_name,
        attributes: JSON.parse(variant.attributes_json || "{}") as Record<string, string>,
        costPrice: variant.cost_price,
        price: variant.price,
        currency: variant.currency,
        stock: variant.stock,
        weight: variant.weight,
        imageUrl: variant.image_url
      })),
      channelConfigs
    }),
    [
      brand,
      attributeRows,
      category,
      categoryId,
      channelConfigs,
      currency,
      description,
      images,
      name,
      productType,
      sku,
      sourcePlatform,
      sourceUrl,
      spu,
      status,
      variants
    ]
  );

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch<{ product: ProductDetail }>(`/api/catalog/products/${id}`, payload)
        : api.post<{ product: ProductDetail }>("/api/catalog/products", payload),
    onSuccess: async (result) => {
      setNotice("商品已保存");
      await queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
      await queryClient.invalidateQueries({ queryKey: ["catalog-product", result.product.id] });
      if (!editing) navigate(`/products/${result.product.id}`);
    }
  });

  const publish = useMutation({
    mutationFn: () =>
      api.post<{ jobs: Array<{ id: string; status: string; message: string }> }>(
        `/api/catalog/products/${id}/publish`,
        { storeIds: selectedStores }
      ),
    onSuccess: async (result) => {
      setNotice(
        result.jobs
          .map((job) => `${job.status}: ${job.message}`)
          .join("；")
      );
      await queryClient.invalidateQueries({ queryKey: ["catalog-product", id] });
      await queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
    },
    onError: (error) => setNotice(error.message)
  });

  const applyTemplate = useMutation({
    mutationFn: () =>
      api.post<{ updates: Array<{ id: string; price: number; listPrice: number; profit: number }> }>(
        `/api/catalog/pricing-templates/${templateId}/apply`,
        { productId: id }
      ),
    onSuccess: async (result) => {
      const priceMap = new Map(result.updates.map((update) => [update.id, update]));
      setVariants((current) =>
        current.map((variant) => {
          const update = variant.id ? priceMap.get(variant.id) : undefined;
          return update ? { ...variant, price: update.price, currency } : variant;
        })
      );
      setNotice("定价模板已应用");
    },
    onError: (error) => setNotice(error.message)
  });

  async function uploadImage(file: File) {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("图片读取失败"));
      reader.readAsDataURL(file);
    });
    const result = await api.post<{ url: string }>("/api/catalog/uploads/images", {
      fileName: file.name,
      dataUrl
    });
    setImages((current) => [...current, { url: result.url, alt: name }]);
  }

  function updateVariant(index: number, patch: Partial<ProductVariant>) {
    setVariants((current) =>
      current.map((variant, variantIndex) =>
        variantIndex === index ? { ...variant, ...patch } : variant
      )
    );
  }

  const categories = categoriesQuery.data?.categories ?? [];
  const categoryTree = useMemo(() => {
    const nodes = new Map<string, CategoryTreeNode>();
    for (const category of categories) {
      nodes.set(category.external_category_id, { ...category, children: [] });
    }
    const roots: CategoryTreeNode[] = [];
    for (const node of nodes.values()) {
      const parent = node.parent_external_category_id
        ? nodes.get(node.parent_external_category_id)
        : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
    const sortNodes = (items: CategoryTreeNode[]) => {
      items.sort((left, right) =>
        (left.local_name || left.name).localeCompare(right.local_name || right.name)
      );
      items.forEach((item) => sortNodes(item.children));
    };
    sortNodes(roots);
    return roots;
  }, [categories]);
  const normalizedCategorySearch = categorySearch.trim().toLowerCase();
  const categorySearchResults = normalizedCategorySearch
    ? categories
        .filter((category) =>
          [
            category.name,
            category.local_name,
            category.external_category_id,
            category.path
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedCategorySearch)
        )
        .slice(0, 300)
    : [];

  function updateChannelConfig(
    storeId: string,
    patch: Partial<Omit<ChannelConfigDraft, "storeId">>
  ) {
    setChannelConfigs((current) => {
      const existing = current.find((config) => config.storeId === storeId);
      if (existing) {
        return current.map((config) =>
          config.storeId === storeId ? { ...config, ...patch } : config
        );
      }
      return [
        ...current,
        {
          storeId,
          categoryId: patch.categoryId ?? "",
          attributes: patch.attributes ?? {}
        }
      ];
    });
  }

  function selectCategory(category: PlatformCategory) {
    if (category.is_leaf !== 1 || !channelStoreId) return;
    updateChannelConfig(channelStoreId, {
      categoryId: category.id,
      attributes: {}
    });
    setCategory(category.local_name || category.name);
    setCategoryId(category.external_category_id);
  }

  function updateChannelAttribute(attributeId: string, value: unknown) {
    if (!channelStoreId) return;
    updateChannelConfig(channelStoreId, {
      attributes: {
        ...(activeChannel?.attributes ?? {}),
        [attributeId]: value
      }
    });
  }

  function renderCategoryNodes(nodes: CategoryTreeNode[], depth = 0): ReactNode {
    return nodes.map((node) => {
      const selected = activeChannel?.categoryId === node.id;
      return (
        <div className="category-tree-node" key={node.id}>
          <button
            type="button"
            className={selected ? "category-tree-row selected" : "category-tree-row"}
            style={{ paddingLeft: `${10 + depth * 16}px` }}
            disabled={node.is_leaf !== 1}
            onClick={() => selectCategory(node)}
            title={node.path}
          >
            <span>{node.local_name || node.name}</span>
            <small>{node.external_category_id}</small>
            {node.is_leaf === 1 ? (
              <em>{node.attribute_count || 0}</em>
            ) : (
              <em className="category-parent-mark">{node.children.length}</em>
            )}
          </button>
          {node.children.length > 0 ? renderCategoryNodes(node.children, depth + 1) : null}
        </div>
      );
    });
  }

  const storeOptions = storesQuery.data?.stores ?? [];

  return (
    <div className="product-editor-page">
      <div className="toolbar-row editor-toolbar">
        <button type="button" className="ghost-button" onClick={() => navigate("/products")}>
          <ArrowLeft size={16} />
          {t("返回商品")}
        </button>
        <div className="button-row">
          <button type="button" className="ghost-button" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save size={16} />
            {save.isPending ? t("保存中...") : t("保存商品")}
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={!editing || selectedStores.length === 0 || publish.isPending}
            onClick={() => publish.mutate()}
          >
            <Send size={16} />
            {publish.isPending ? t("刊登中...") : t("保存并刊登")}
          </button>
        </div>
      </div>

      {notice ? <div className="notice success-notice">{t(notice)}</div> : null}
      {productQuery.isError ? <div className="notice error-notice">{productQuery.error.message}</div> : null}

      <div className="editor-shell">
        <div className="editor-main">
          <div className="editor-tabs">
            {([
              ["basic", "基本信息"],
              ["channel", "平台类目与属性"],
              ["images", "产品图片"],
              ["variants", "销售规格与 SKU"],
              ["pricing", "定价模板"],
              ["publish", "刊登店铺"]
            ] as Array<[EditorTab, string]>).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={tab === key ? "editor-tab active" : "editor-tab"}
                onClick={() => setTab(key as EditorTab)}
              >
                {t(label)}
              </button>
            ))}
          </div>

          {tab === "basic" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("基本信息")}</h2>
                  <span>{t("填写平台刊登需要的标题、类目、品牌和描述。")}</span>
                </div>
              </div>
              <div className="form-grid">
                <label className="field span-2">
                  <span>{t("产品标题")}</span>
                  <input value={name} onChange={(event) => setName(event.target.value)} />
                </label>
                <label className="field">
                  <span>SPU</span>
                  <input value={spu} onChange={(event) => setSpu(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("主 SKU")}</span>
                  <input value={sku} onChange={(event) => setSku(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("类目")}</span>
                  <input value={category} onChange={(event) => setCategory(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("平台类目 ID")}</span>
                  <input value={categoryId} onChange={(event) => setCategoryId(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("品牌")}</span>
                  <input value={brand} onChange={(event) => setBrand(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("产品类型")}</span>
                  <input value={productType} onChange={(event) => setProductType(event.target.value)} placeholder="PRODUCT / 平台 productType" />
                </label>
                <label className="field">
                  <span>{t("币种")}</span>
                  <input value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} maxLength={3} />
                </label>
                <label className="field">
                  <span>{t("状态")}</span>
                  <select value={status} onChange={(event) => setStatus(event.target.value)}>
                    <option value="draft">{t("草稿")}</option>
                    <option value="active">{t("在售中")}</option>
                    <option value="archived">{t("已归档")}</option>
                  </select>
                </label>
                <label className="field">
                  <span>{t("来源链接")}</span>
                  <input value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} />
                </label>
                <label className="field">
                  <span>{t("来源平台")}</span>
                  <input value={sourcePlatform} onChange={(event) => setSourcePlatform(event.target.value)} />
                </label>
                <label className="field span-2">
                  <span>{t("产品描述")}</span>
                  <textarea className="editor-textarea" rows={8} value={description} onChange={(event) => setDescription(event.target.value)} />
                </label>
              </div>
              <div className="attribute-section">
                <div className="attribute-head">
                  <div>
                    <strong>{t("平台属性")}</strong>
                    <span>{t("按平台类目要求填写属性，例如材质、颜色、尺码、适用人群。")}</span>
                  </div>
                  <button
                    type="button"
                    className="ghost-button compact-button"
                    onClick={() => setAttributeRows((current) => [...current, { key: "", value: "" }])}
                  >
                    <Plus size={15} />
                    {t("添加属性")}
                  </button>
                </div>
                <div className="attribute-list">
                  {attributeRows.map((row, index) => (
                    <div className="attribute-row" key={index}>
                      <input
                        value={row.key}
                        placeholder={t("属性名")}
                        onChange={(event) =>
                          setAttributeRows((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, key: event.target.value } : item
                            )
                          )
                        }
                      />
                      <input
                        value={row.value}
                        placeholder={t("属性值")}
                        onChange={(event) =>
                          setAttributeRows((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, value: event.target.value } : item
                            )
                          )
                        }
                      />
                      <button
                        type="button"
                        className="icon-button danger-button"
                        onClick={() =>
                          setAttributeRows((current) =>
                            current.length > 1 ? current.filter((_, itemIndex) => itemIndex !== index) : current
                          )
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {tab === "channel" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("平台类目与属性")}</h2>
                  <span>
                    {t(
                      "每个刊登店铺分别选择叶子类目，并按照平台属性模板填写必填属性。刊登前系统会再次校验。"
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className="ghost-button"
                  disabled={!activeStore || syncCategories.isPending}
                  onClick={() => syncCategories.mutate()}
                >
                  <RefreshCw className={syncCategories.isPending ? "spin" : ""} size={16} />
                  {syncCategories.isPending ? t("同步中...") : t("同步类目树")}
                </button>
              </div>

              <div className="channel-toolbar">
                <label className="field">
                  <span>{t("刊登店铺")}</span>
                  <select
                    value={channelStoreId}
                    onChange={(event) => {
                      setChannelStoreId(event.target.value);
                      setCategorySearch("");
                    }}
                  >
                    <option value="">{t("请选择店铺")}</option>
                    {storeOptions.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name} · {platformLabel(store.platform)} · {store.region}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t("搜索类目")}</span>
                  <input
                    value={categorySearch}
                    onChange={(event) => setCategorySearch(event.target.value)}
                    placeholder={t("输入类目名称或平台类目 ID")}
                  />
                </label>
                <div className="selected-category-summary">
                  <span>{t("当前类目")}</span>
                  <strong>
                    {attributesQuery.data?.category
                      ? attributesQuery.data.category.local_name ||
                        attributesQuery.data.category.name
                      : t("尚未选择")}
                  </strong>
                  <small>
                    {attributesQuery.data?.category?.external_category_id || "-"}
                  </small>
                </div>
              </div>

              {categoriesQuery.isError ? (
                <div className="notice error-notice">{categoriesQuery.error.message}</div>
              ) : null}

              <div className="channel-category-layout">
                <div className="category-browser">
                  <div className="category-browser-head">
                    <strong>{t("类目树")}</strong>
                    <span>{t("仅叶子类目可以绑定商品")}</span>
                  </div>
                  {categoriesQuery.isLoading ? (
                    <div className="loading-inline">
                      <LoaderCircle className="spin" size={16} />
                      {t("加载中...")}
                    </div>
                  ) : null}
                  {!categoriesQuery.isLoading && categories.length === 0 ? (
                    <div className="empty-state compact-empty-state">
                      <RefreshCw size={24} />
                      <strong>{t("当前店铺还没有类目数据")}</strong>
                      <span>{t("点击“同步类目树”从平台读取类目。")}</span>
                    </div>
                  ) : null}
                  {normalizedCategorySearch && categorySearchResults.length > 0 ? (
                    <div className="category-search-results">
                      {categorySearchResults.map((category) => (
                        <button
                          type="button"
                          className={
                            activeChannel?.categoryId === category.id
                              ? "category-tree-row selected"
                              : "category-tree-row"
                          }
                          key={category.id}
                          disabled={category.is_leaf !== 1}
                          onClick={() => selectCategory(category)}
                        >
                          <span>{category.path || category.local_name || category.name}</span>
                          <small>{category.external_category_id}</small>
                          <em>{category.attribute_count || 0}</em>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {!normalizedCategorySearch && categoryTree.length > 0 ? (
                    <div className="category-tree">{renderCategoryNodes(categoryTree)}</div>
                  ) : null}
                  {normalizedCategorySearch && categorySearchResults.length === 0 ? (
                    <div className="empty-state compact-empty-state">
                      <strong>{t("没有匹配的类目")}</strong>
                    </div>
                  ) : null}
                </div>

                <div className="channel-attribute-panel">
                  <div className="category-browser-head">
                    <div>
                      <strong>{t("类目属性模板")}</strong>
                      <span>
                        {t("带 * 的属性是平台必填项。属性变化后请保存商品。")}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ghost-button compact-button"
                      disabled={!activeChannel?.categoryId || syncAttributes.isPending}
                      onClick={() => syncAttributes.mutate()}
                    >
                      <RefreshCw className={syncAttributes.isPending ? "spin" : ""} size={15} />
                      {syncAttributes.isPending ? t("同步中...") : t("同步属性")}
                    </button>
                  </div>
                  {!activeChannel?.categoryId ? (
                    <div className="empty-state compact-empty-state">
                      <strong>{t("请先从左侧选择叶子类目")}</strong>
                    </div>
                  ) : null}
                  {attributesQuery.isLoading ? (
                    <div className="loading-inline">
                      <LoaderCircle className="spin" size={16} />
                      {t("正在读取属性模板...")}
                    </div>
                  ) : null}
                  {attributesQuery.isError ? (
                    <div className="notice error-notice">{attributesQuery.error.message}</div>
                  ) : null}
                  {!attributesQuery.isLoading &&
                  activeChannel?.categoryId &&
                  (attributesQuery.data?.attributes.length ?? 0) === 0 ? (
                    <div className="empty-state compact-empty-state">
                      <strong>{t("该平台未返回属性，或尚未同步属性模板")}</strong>
                      <span>{t("点击“同步属性”重试。")}</span>
                    </div>
                  ) : null}
                  <div className="channel-attribute-list">
                    {(attributesQuery.data?.attributes ?? []).map((attribute) => {
                      const options =
                        attribute.options.length > 0
                          ? attribute.options
                          : attribute.values.map((value) => ({
                              externalId: value.external_value_id,
                              name: value.name,
                              localName: value.local_name,
                              sortOrder: value.sort_order
                            }));
                      const value =
                        activeChannel?.attributes[attribute.external_attribute_id];
                      const label = attribute.local_name || attribute.name;
                      return (
                        <label className="field channel-attribute-field" key={attribute.id}>
                          <span>
                            {label}
                            {attribute.required === 1 ? <b className="required-mark">*</b> : null}
                            {attribute.unit ? <small>{attribute.unit}</small> : null}
                          </span>
                          {options.length > 0 && attribute.multiple === 1 ? (
                            <select
                              multiple
                              value={
                                Array.isArray(value) ? value.map((item) => String(item)) : []
                              }
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  Array.from(event.target.selectedOptions, (option) => option.value)
                                )
                              }
                            >
                              {options.map((option) => (
                                <option key={option.externalId} value={option.externalId}>
                                  {option.localName || option.name}
                                </option>
                              ))}
                            </select>
                          ) : options.length > 0 ? (
                            <select
                              value={typeof value === "string" ? value : ""}
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  event.target.value
                                )
                              }
                            >
                              <option value="">{t("请选择")}</option>
                              {options.map((option) => (
                                <option key={option.externalId} value={option.externalId}>
                                  {option.localName || option.name}
                                </option>
                              ))}
                            </select>
                          ) : attribute.input_type === "checkbox" ? (
                            <input
                              type="checkbox"
                              checked={value === true || value === "true"}
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  event.target.checked
                                )
                              }
                            />
                          ) : attribute.input_type === "number" ? (
                            <input
                              type="number"
                              step="any"
                              value={typeof value === "number" ? value : Number(value || 0)}
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  Number(event.target.value)
                                )
                              }
                            />
                          ) : attribute.input_type === "textarea" ? (
                            <textarea
                              rows={3}
                              value={typeof value === "string" ? value : ""}
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  event.target.value
                                )
                              }
                            />
                          ) : (
                            <input
                              value={typeof value === "string" ? value : ""}
                              onChange={(event) =>
                                updateChannelAttribute(
                                  attribute.external_attribute_id,
                                  event.target.value
                                )
                              }
                            />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {tab === "images" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("产品图片")}</h2>
                  <span>{t("建议上传 1200×1200 的 JPG、PNG 或 WEBP 图片，第一张作为主图。")}</span>
                </div>
                <label className="primary-button upload-button">
                  <UploadCloud size={16} />
                  {t("上传图片")}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    onChange={(event) => {
                      const files = Array.from(event.target.files ?? []);
                      files.forEach((file) => void uploadImage(file));
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>
              <div className="product-image-grid">
                {images.map((image, index) => (
                  <div className="product-image-card" key={`${image.url}-${index}`}>
                    <img src={image.url} alt={image.alt || name} />
                    {index === 0 ? <span className="image-primary">{t("主图")}</span> : null}
                    <button
                      type="button"
                      className="image-remove"
                      onClick={() => setImages((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                {images.length === 0 ? (
                  <div className="empty-image-card">
                    <ImagePlus size={28} />
                    <span>{t("还没有产品图片")}</span>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {tab === "variants" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("销售规格与 SKU")}</h2>
                  <span>{t("每个 SKU 可以单独设置成本、售价、库存、重量和图片。")}</span>
                </div>
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() =>
                    setVariants((current) => [
                      ...current,
                      emptyVariant(`${sku || "SKU"}-${current.length + 1}`, currency)
                    ])
                  }
                >
                  <Plus size={16} />
                  {t("新增 SKU")}
                </button>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>{t("规格名称")}</th>
                      <th>{t("成本")}</th>
                      <th>{t("售价")}</th>
                      <th>{t("库存")}</th>
                      <th>{t("重量 KG")}</th>
                      <th>{t("操作")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((variant, index) => (
                      <tr key={variant.id ?? index}>
                        <td><input className="table-input" value={variant.sku} onChange={(event) => updateVariant(index, { sku: event.target.value })} /></td>
                        <td><input className="table-input" value={variant.variant_name} onChange={(event) => updateVariant(index, { variant_name: event.target.value })} /></td>
                        <td><input className="table-input narrow" type="number" step="0.01" value={variant.cost_price} onChange={(event) => updateVariant(index, { cost_price: Number(event.target.value) })} /></td>
                        <td><input className="table-input narrow" type="number" step="0.01" value={variant.price} onChange={(event) => updateVariant(index, { price: Number(event.target.value) })} /></td>
                        <td><input className="table-input narrow" type="number" value={variant.stock} onChange={(event) => updateVariant(index, { stock: Number(event.target.value) })} /></td>
                        <td><input className="table-input narrow" type="number" step="0.01" value={variant.weight} onChange={(event) => updateVariant(index, { weight: Number(event.target.value) })} /></td>
                        <td>
                          <button
                            type="button"
                            className="icon-button danger-button"
                            disabled={variants.length <= 1}
                            onClick={() => setVariants((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {tab === "pricing" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("定价模板")}</h2>
                  <span>{t("应用模板后会按照成本、运费、佣金、利润率和折扣重新计算 SKU 售价。")}</span>
                </div>
              </div>
              <div className="pricing-apply-row">
                <label className="field">
                  <span>{t("选择模板")}</span>
                  <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
                    <option value="">{t("请选择模板")}</option>
                    {(templatesQuery.data?.templates ?? []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} · {template.currency} · {template.profit_rate}%
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="primary-button"
                  disabled={!editing || !templateId || applyTemplate.isPending}
                  onClick={() => applyTemplate.mutate()}
                >
                  <Calculator size={16} />
                  {applyTemplate.isPending ? t("计算中...") : t("应用到 SKU")}
                </button>
              </div>
              <div className="pricing-preview-grid">
                {variants.map((variant) => (
                  <div className="pricing-preview-card" key={variant.id ?? variant.sku}>
                    <strong>{variant.sku}</strong>
                    <span>{t("成本")} {money(variant.cost_price, variant.currency)}</span>
                    <span>{t("售价")} {money(variant.price, variant.currency)}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {tab === "publish" ? (
            <section className="panel editor-panel">
              <div className="panel-header">
                <div>
                  <h2>{t("刊登店铺")}</h2>
                  <span>{t("选择已授权店铺后保存并刊登，系统会创建刊登任务并记录平台返回结果。")}</span>
                </div>
              </div>
              <div className="publish-store-list">
                {storeOptions.map((store) => (
                  <label className="publish-store-row" key={store.id}>
                    <input
                      type="checkbox"
                      checked={selectedStores.includes(store.id)}
                      onChange={(event) =>
                        setSelectedStores((current) =>
                          event.target.checked
                            ? [...current, store.id]
                            : current.filter((storeId) => storeId !== store.id)
                        )
                      }
                    />
                    <span className={`platform-dot dot-${store.platform}`} />
                    <strong>{store.name}</strong>
                    <small>{platformLabel(store.platform)} · {store.region}</small>
                    <StatusBadge tone={statusTone(store.status)}>{t(statusLabel(store.status))}</StatusBadge>
                  </label>
                ))}
              </div>
              {productQuery.data?.product.publishJobs.length ? (
                <div className="publish-job-list">
                  {productQuery.data.product.publishJobs.map((job) => (
                    <div className="publish-job-row" key={job.id}>
                      <span className={`platform-dot dot-${job.platform}`} />
                      <strong>{platformLabel(job.platform)}</strong>
                      <StatusBadge tone={statusTone(job.status)}>{t(statusLabel(job.status))}</StatusBadge>
                      <span>{job.message}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}
        </div>

        <aside className="editor-aside">
          <div className="panel editor-summary">
            <h3>{t("商品摘要")}</h3>
            <div className="summary-row"><span>{t("主 SKU")}</span><strong>{sku || "-"}</strong></div>
            <div className="summary-row"><span>{t("SKU 数量")}</span><strong>{variants.length}</strong></div>
            <div className="summary-row"><span>{t("图片数量")}</span><strong>{images.length}</strong></div>
            <div className="summary-row"><span>{t("最低售价")}</span><strong>{money(Math.min(...variants.map((variant) => variant.price || 0)), currency)}</strong></div>
            <div className="summary-row"><span>{t("总库存")}</span><strong>{variants.reduce((sum, variant) => sum + Number(variant.stock || 0), 0)}</strong></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
